import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

import {
  FACILITY_CLASSIFICATIONS,
  type FacilityClassification,
  type ModuleId,
  findModuleForRoute,
  getDefaultModulesForClassification,
  moduleRegistry,
} from "../config/moduleRegistry"
import { findFacilityById, useFacilityDirectory, type FacilityRecord } from "../config/facilities"
import { facilityModulesApi } from "./api/handlers/facilityModules"
import { ApiError } from "./api/http"
import { PATHS } from "../routes/paths"

type FacilitySelection = {
  facilityId: string
  classification: FacilityClassification
}

type ModulesSource = "api" | "default"

type FacilityAccessContextValue = {
  facilityId: string
  classification: FacilityClassification
  modules: ModuleId[]
  loading: boolean
  error: string | null
  isUsingDefaults: boolean
  pendingAuthorization: boolean
  canUseModule: (id: ModuleId) => boolean
  canAccessRoute: (route: keyof typeof PATHS) => boolean
  setFacility: (facilityId: string, classification?: FacilityClassification) => void
  refresh: () => void
  saveModules: (
    modules: ModuleId[],
    classification?: FacilityClassification,
  ) => Promise<FacilityModulesDto>
}

type FacilityModulesDto = {
  facilityId: string
  classification?: FacilityClassification | string | null
  modules: ModuleId[] | string[]
  updatedAt?: string
  pendingAuthorization?: boolean
}

const STORAGE_KEY = "phmis.facilityAccess.selection"
const MODULES_CACHE_PREFIX = "phmis.facilityAccess.modules"

const FacilityAccessContext = createContext<FacilityAccessContextValue | undefined>(undefined)

const KNOWN_CLASSIFICATIONS = new Set<FacilityClassification>(FACILITY_CLASSIFICATIONS.map((c) => c.id))
const KNOWN_MODULE_IDS = new Set<ModuleId>(moduleRegistry.map((m) => m.id as ModuleId))

const FALLBACK_CLASSIFICATION = (FACILITY_CLASSIFICATIONS[0]?.id ?? "land-border") as FacilityClassification

type CachedModulesSnapshot = {
  facilityId: string
  classification: FacilityClassification
  modules: ModuleId[]
  updatedAt?: number
}

const buildModulesCacheKey = (facilityId: string) => `${MODULES_CACHE_PREFIX}.${facilityId}`

function safeParseSelection(raw: string | null): FacilitySelection | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<FacilitySelection> | null
    if (!parsed || typeof parsed !== "object") return null
    const facilityId = typeof parsed.facilityId === "string" && parsed.facilityId ? parsed.facilityId : null
    const classificationCandidate = normalizeClassification(parsed.classification)
    if (!facilityId) return null
    const facility = findFacilityById(facilityId)
    const classification =
      classificationCandidate ?? normalizeClassification(facility?.classification) ?? FALLBACK_CLASSIFICATION
    return { facilityId, classification }
  } catch {
    return null
  }
}

function readStoredSelection(): FacilitySelection | null {
  if (typeof window === "undefined") return null
  try {
    return safeParseSelection(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

function readCachedModules(facilityId: string): CachedModulesSnapshot | null {
  if (typeof window === "undefined") return null
  if (!facilityId) return null
  try {
    const raw = window.localStorage.getItem(buildModulesCacheKey(facilityId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CachedModulesSnapshot> | null
    if (!parsed || typeof parsed !== "object") return null
    const classification = normalizeClassification(parsed.classification) ?? FALLBACK_CLASSIFICATION
    const modules = normalizeModules(parsed.modules ?? [])
    return { facilityId, classification, modules, updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : undefined }
  } catch {
    return null
  }
}

function persistCachedModules(facilityId: string, classification: FacilityClassification, modules: ModuleId[]) {
  if (typeof window === "undefined") return
  if (!facilityId) return
  try {
    const snapshot: CachedModulesSnapshot = {
      facilityId,
      classification,
      modules,
      updatedAt: Date.now(),
    }
    window.localStorage.setItem(buildModulesCacheKey(facilityId), JSON.stringify(snapshot))
  } catch {
    // ignore storage issues
  }
}

function normalizeClassification(value: unknown): FacilityClassification | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return KNOWN_CLASSIFICATIONS.has(trimmed as FacilityClassification) ? (trimmed as FacilityClassification) : undefined
}

function normalizeModules(values: readonly (string | ModuleId)[] | null | undefined): ModuleId[] {
  if (!values) return []
  const seen = new Set<ModuleId>()
  const result: ModuleId[] = []
  values.forEach((value) => {
    const id = value as ModuleId
    if (KNOWN_MODULE_IDS.has(id) && !seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  })
  return result
}

function mergeWithDefaults(modules: readonly ModuleId[], defaults: readonly ModuleId[]): ModuleId[] {
  if (!defaults.length) return [...modules]
  const merged: ModuleId[] = [...modules]
  const seen = new Set<ModuleId>(modules)
  defaults.forEach((module) => {
    if (!seen.has(module)) {
      merged.push(module)
      seen.add(module)
    }
  })
  return merged
}

function determineClassification(
  facilities: readonly FacilityRecord[],
  facilityId: string,
  classification?: unknown,
  fallback?: unknown,
): FacilityClassification {
  const explicit = normalizeClassification(classification)
  if (explicit) return explicit
  const facility = findFacilityById(facilityId, facilities)
  const fromFacility = normalizeClassification(facility?.classification)
  if (fromFacility) return fromFacility
  const fromFallback = normalizeClassification(fallback)
  if (fromFallback) return fromFallback
  return FALLBACK_CLASSIFICATION
}

function resolveSelection(
  facilities: readonly FacilityRecord[],
  facilityId: string,
  classification?: unknown,
  fallback?: unknown,
): FacilitySelection {
  const resolvedClassification = determineClassification(facilities, facilityId, classification, fallback)
  return { facilityId, classification: resolvedClassification }
}

export function FacilityAccessProvider({ children }: { children: React.ReactNode }) {
  const initialSelection = useMemo(() => {
    return readStoredSelection() ?? { facilityId: "", classification: FALLBACK_CLASSIFICATION }
  }, [])

  const { facilities, loading: facilitiesLoading, error: facilitiesError, refresh: refreshFacilities } =
    useFacilityDirectory()

  const [selection, setSelection] = useState<FacilitySelection>(initialSelection)
  const [modules, setModules] = useState<ModuleId[]>(() =>
    getDefaultModulesForClassification(initialSelection.classification),
  )
  const [source, setSource] = useState<ModulesSource>("default")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)
  const [pendingAuthorization, setPendingAuthorization] = useState(false)

  useEffect(() => {
    if (!facilities.length) {
      return
    }
    setSelection((prev) => {
      if (prev.facilityId) {
        const existing = findFacilityById(prev.facilityId, facilities)
        if (existing) {
          const resolved = determineClassification(
            facilities,
            prev.facilityId,
            prev.classification,
            FALLBACK_CLASSIFICATION,
          )
          if (resolved !== prev.classification) {
            return { facilityId: prev.facilityId, classification: resolved }
          }
          return prev
        }
      }
      const fallback = facilities[0]
      const resolved = determineClassification(facilities, fallback.id, fallback.classification, prev.classification)
      if (fallback.id === prev.facilityId && resolved === prev.classification) {
        return prev
      }
      return { facilityId: fallback.id, classification: resolved }
    })
  }, [facilities])

  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection))
    } catch {
      // ignore storage errors (private mode, etc.)
    }
  }, [selection])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!selection.facilityId) {
        setModules(getDefaultModulesForClassification(selection.classification))
        setSource("default")
        setError(null)
        setPendingAuthorization(false)
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const response = await facilityModulesApi.get(selection.facilityId)
        if (!active) return
        const nextClassification = normalizeClassification(response.classification) ?? selection.classification
        const defaults = getDefaultModulesForClassification(nextClassification)
        const nextModules = mergeWithDefaults(normalizeModules(response.modules), defaults)
        setSelection((prev) => ({ facilityId: prev.facilityId, classification: nextClassification }))
        setModules(nextModules.length ? nextModules : defaults)
        setSource("api")
        setError(null)
        setPendingAuthorization(Boolean(response.pendingAuthorization))
        persistCachedModules(selection.facilityId, nextClassification, nextModules.length ? nextModules : defaults)
      } catch (err) {
        if (!active) return
        const cached = readCachedModules(selection.facilityId)
        if (cached) {
          const defaults = getDefaultModulesForClassification(cached.classification)
          const merged = mergeWithDefaults(cached.modules, defaults)
          setSelection((prev) => ({ facilityId: prev.facilityId, classification: cached.classification }))
          setModules(merged.length ? merged : defaults)
          setSource("default")
          setError(null)
          setPendingAuthorization(false)
        } else {
          const defaults = getDefaultModulesForClassification(selection.classification)
          if (err instanceof ApiError && err.status === 404) {
            setModules(defaults)
            setSource("default")
            setError(null)
            setPendingAuthorization(false)
          } else {
            setModules(defaults)
            setSource("default")
            setError(err instanceof Error ? err.message : "Failed to load facility module settings")
            setPendingAuthorization(false)
          }
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    load().catch(() => {
      /* handled above */
    })
    return () => {
      active = false
    }
  }, [selection.facilityId, selection.classification, refreshToken])

  const canUseModule = useCallback(
    (id: ModuleId) => {
      return modules.includes(id)
    },
    [modules],
  )

  const canAccessRoute = useCallback(
    (route: keyof typeof PATHS) => {
      const module = findModuleForRoute(route)
      if (!module) return true
      return canUseModule(module.id as ModuleId)
    },
    [canUseModule],
  )

  const setFacility = useCallback(
    (facilityId: string, classification?: FacilityClassification) => {
      const resolvedClassification = determineClassification(
        facilities,
        facilityId,
        classification,
        selection.classification,
      )
      setSelection({ facilityId, classification: resolvedClassification })
      setModules(getDefaultModulesForClassification(resolvedClassification))
      setSource("default")
      setError(null)
      setPendingAuthorization(false)
    },
    [facilities, selection.classification],
  )

  const refresh = useCallback(() => {
    refreshFacilities().catch(() => {
      /* facility errors handled via state */
    })
    setRefreshToken((token) => token + 1)
  }, [refreshFacilities])

  const saveModules = useCallback(
    async (nextModules: ModuleId[], classification?: FacilityClassification) => {
      const payloadClassification = classification ?? selection.classification
      const normalizedInput = normalizeModules(nextModules)
      const payloadModules = mergeWithDefaults(
        normalizedInput,
        getDefaultModulesForClassification(payloadClassification),
      )
      const response = await facilityModulesApi.update(selection.facilityId, {
        modules: payloadModules,
        classification: payloadClassification,
      })
      const normalizedClassification = normalizeClassification(response.classification) ?? payloadClassification
      const normalizedModules = mergeWithDefaults(
        normalizeModules(response.modules),
        getDefaultModulesForClassification(normalizedClassification),
      )
      setSelection({ facilityId: selection.facilityId, classification: normalizedClassification })
      setModules(normalizedModules)
      setSource("api")
      setError(null)
      setPendingAuthorization(Boolean(response.pendingAuthorization))
      persistCachedModules(selection.facilityId, normalizedClassification, normalizedModules)
      return response
    },
    [selection],
  )

  const combinedLoading = loading || facilitiesLoading
  const combinedError = facilitiesError ?? error

  const value = useMemo<FacilityAccessContextValue>(() => {
    return {
      facilityId: selection.facilityId,
      classification: selection.classification,
      modules,
      loading: combinedLoading,
      error: combinedError,
      isUsingDefaults: source === "default",
      pendingAuthorization,
      canUseModule,
      canAccessRoute,
      setFacility,
      refresh,
      saveModules,
    }
  }, [
    selection,
    modules,
    combinedLoading,
    combinedError,
    source,
    pendingAuthorization,
    canUseModule,
    canAccessRoute,
    setFacility,
    refresh,
    saveModules,
  ])

  return <FacilityAccessContext.Provider value={value}>{children}</FacilityAccessContext.Provider>
}

export function useFacilityAccess(): FacilityAccessContextValue {
  const ctx = useContext(FacilityAccessContext)
  if (!ctx) {
    throw new Error("useFacilityAccess must be used within a FacilityAccessProvider")
  }
  return ctx
}

export function useFacilityAccessOrNull(): FacilityAccessContextValue | null {
  return useContext(FacilityAccessContext) ?? null
}
